import { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import BadgeIcon from '@mui/icons-material/Badge';
import AppsIcon from '@mui/icons-material/Apps';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { OidcProtectedComponent, useCanAccess, useOidc } from '@nohara/react-oidc';
import { permission } from '@nohara/oidc-core';

import { LazyLayout } from '../ui';

import { UserSection } from '@/features/users/components/user-section';
import { paths } from '@/config/paths';
import { LanguageSwitcher } from '@/components/language';
import { ThemeToggle } from '@/components/theme';
import { useOrg, useUserOrgs } from '@/features/orgs/api';

const drawerWidth = 240;

type OrgLayoutProps = {
  children: React.ReactNode;
  orgId?: string;
};

const adminPermissions = [
  permission.org.read,
  permission.app.read,
  permission.role.read,
  permission.user.read,
];

const readAppPermissions = [permission.app.read];
const readRolePermissions = [permission.role.read];
const readUserPermissions = [permission.user.read];
const updateOrgPermissions = [permission.org.update];

export const OrgLayout = ({ children, orgId }: OrgLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { userId } = useOidc();
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { data: orgData } = useOrg(orgId);
  const { data: orgs } = useUserOrgs(userId);
  const { canAccess: isAdmin } = useCanAccess({
    requireAny: adminPermissions,
  });

  const hasMoreThanOneOrg = !!orgs?.length && orgs.length > 1;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Stack height='100vh'>
      <Toolbar
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant='h6' noWrap component='div'>
          {orgData?.name || t('navigation.orgTitle')}
        </Typography>
        <Box display='flex' alignItems='center'>
          {orgId && (
            <>
              <Chip label={`ID: ${orgId.substring(0, 24)}`} size='small' sx={{ ml: -2 }} />
              <IconButton
                size='small'
                onClick={() => {
                  navigator.clipboard.writeText(orgId);
                }}
                title={t('common.copyToClipboard')}
              >
                <ContentCopyIcon fontSize='small' />
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>
      {(hasMoreThanOneOrg || isAdmin) && (
        <>
          <Divider />
          <List>
            {orgId && hasMoreThanOneOrg && (
              <ListItem disablePadding component={Link} to={paths.home.path} viewTransition>
                <ListItemButton>
                  <ListItemIcon>
                    <SwitchAccountIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.menu.switchOrg')} />
                </ListItemButton>
              </ListItem>
            )}
            {isAdmin && (
              <ListItem disablePadding component={Link} to={paths.admin.root.path} viewTransition>
                <ListItemButton>
                  <ListItemIcon>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.menu.adminPanel')} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </>
      )}
      <Divider />
      {orgId && (
        <>
          <List>
            <ListItem
              disablePadding
              component={Link}
              to={paths.org.dashboard.getHref(orgId)}
              viewTransition
            >
              <ListItemButton>
                <ListItemIcon>
                  <DashboardIcon />
                </ListItemIcon>
                <ListItemText primary={t('navigation.menu.dashboard')} />
              </ListItemButton>
            </ListItem>
            <OidcProtectedComponent requireAll={readAppPermissions} context={{ orgId }}>
              <ListItem
                disablePadding
                component={Link}
                to={paths.org.apps.getHref(orgId)}
                viewTransition
              >
                <ListItemButton>
                  <ListItemIcon>
                    <AppsIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.menu.apps')} />
                </ListItemButton>
              </ListItem>
            </OidcProtectedComponent>
            <OidcProtectedComponent requireAll={readRolePermissions} context={{ orgId }}>
              <ListItem
                disablePadding
                component={Link}
                to={paths.org.roles.getHref(orgId)}
                viewTransition
              >
                <ListItemButton>
                  <ListItemIcon>
                    <BadgeIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.menu.roles')} />
                </ListItemButton>
              </ListItem>
            </OidcProtectedComponent>
            <OidcProtectedComponent requireAll={readUserPermissions} context={{ orgId }}>
              <ListItem
                disablePadding
                component={Link}
                to={paths.org.users.getHref(orgId)}
                viewTransition
              >
                <ListItemButton>
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.menu.users')} />
                </ListItemButton>
              </ListItem>
            </OidcProtectedComponent>
            <OidcProtectedComponent requireAll={updateOrgPermissions} context={{ orgId }}>
              <ListItem
                disablePadding
                component={Link}
                to={paths.org.orgProfile.getHref(orgId)}
                viewTransition
              >
                <ListItemButton>
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.menu.orgProfile')} />
                </ListItemButton>
              </ListItem>
            </OidcProtectedComponent>
          </List>
        </>
      )}
      <Stack flex={1} />
      <Divider />
      <UserSection
        userId={userId}
        profilePath={paths.org.userProfile.getHref(orgId)}
        logoutPath={paths.auth.login.path}
      />
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position='fixed'
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'primary.dark',
        }}
      >
        <Toolbar>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant='h6' noWrap component='div' sx={{ flexGrow: 1 }}>
            {orgData?.name || t('navigation.orgAppTitle')}
          </Typography>
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }} gap={2}>
            <ThemeToggle />
            <LanguageSwitcher />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component='nav'
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label='mailbox folders'
      >
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component='main'
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {(navigation.state === 'loading' && <LazyLayout />) || children}
      </Box>
    </Box>
  );
};
